
import { defineComponent } from '@vue/composition-api';
import {
  PreviewOdd,
  PreviewOutcome,
  PreviewEventGroup,
  Tournament,
  LiveEventDetailsEvent,
  MarketGroup
} from '../../../../types/sport';
import icons from '../components/icons.vue';
import SportIcons from '../components/SportIcons.vue';
import { formatDateAndTime } from '@/composables/utils';
import IllinoisTabBar from '../components/TabBar.vue';
import OddsExpandableCard from '../components/OddsComponents/OddsExpandibleCard.vue';
import useOdds from '@/composables/useOdds';
import { GenericOption } from '../../../../types/components';
import { getCorrectData, addOddToCartV3FromLive } from '@/composables/useOdds';
import ChangePageModal from '../components/ChangePageModal.vue';
import { LiveEventsCountResult } from '../../../../types/sport';
import {
  formattedDate,
  formatTime,
  formatSecondsToTimeWithMinutes
} from '@/composables/utils';
import FieldStatistics from '../components/Stats/FieldStatistics.vue';
import LiveTrackerIllinois from '../components/LiveTrackerIllinois.vue';
import EventItem from '../components/EventItem.vue';
import EventGroup from '../components/EventGroup.vue';

export default defineComponent({
  name: 'LiveDetailsPage',
  props: {
    eventId: {
      type: String,
      required: true
    },
    sportId: {
      type: String,
      required: true
    },
    categoryId: {
      type: String,
      required: true
    },
    tournamentId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      loading: true,
      error: null as string | null,
      activeMarketGroup: null as string | null,
      urlCloudfront:
        process.env.VUE_APP_URL_CLOUDFRONT + '/illinoisSlider/event-bg.webp',
      correctMarket: '5185578776' as string | null,
      correctMarket2: '7836838694' as string | null,
      openChangeGameMenu: false,
      showEventsList: false,
      isEventsListLoading: false,
      activeTab: 'statistics',
      showTournamentsList: false,
      isTournamentsListLoading: false,
      tournamentSelectedId: this.tournamentId,
      tournamentSelectedName: '',
      sportIdSelected: this.sportId,
      openTitleMenu: false,
      isGroupOddsLoading: false,
      windowHeight: window.innerHeight
    };
  },
  components: {
    icons,
    IllinoisTabBar,
    OddsExpandableCard,
    ChangePageModal,
    LiveTrackerIllinois,
    EventItem,
    EventGroup
  },

  methods: {
    addOddToCartV3FromLive,
    hasOddGroupData(oddGroup: PreviewOdd[]): boolean {
      const selected = this.getOddsForSpread(oddGroup);
      const outcomes = (selected && (selected as any).outcome) || [];
      return Array.isArray(outcomes)
        ? outcomes.some((o: any) => o && o.outcome_odd_value != null)
        : false;
    },
    async fetchEventsByTournament(payload: {
      sportId: string;
      categoryId: string;
      tournamentId: string;
    }) {
      this.isEventsListLoading = true;
      await this.$store.dispatch(
        'IllinoisSport/fetchEventsByTournament',
        payload
      );
      this.isEventsListLoading = false;
    },
    handleChangeTab(tab: string) {
      this.activeMarketGroup = tab;
    },
    t(key: string, values?: Record<string, unknown> | any[]) {
      return this.$t(key, values) as string;
    },
    getCorrectData,
    formattedDate,
    formatDateAndTime,
    useOdds,
    formatTime,
    formatSecondsToTimeWithMinutes,
    buildSpreadOptions(odds: PreviewOdd[]): { label: string; value: string }[] {
      return (odds || []).map((odd: any) => {
        const spread =
          odd?.specifier && odd.specifier !== 'noSpec'
            ? String(odd.specifier)
            : '';
        return {
          label: spread,
          value: spread
        };
      });
    },
    distributeIntoColumns(
      items: PreviewOutcome[],
      numCols: number
    ): PreviewOutcome[][] {
      const columns: PreviewOutcome[][] = [];
      if (!items || items.length === 0 || numCols <= 0) return columns;
      const total = items.length;
      const base = Math.floor(total / numCols);
      const remainder = total % numCols;
      let start = 0;
      for (let i = 0; i < numCols; i++) {
        const size = base + (i < remainder ? 1 : 0);
        const end = start + size;
        if (size > 0) {
          columns.push(items.slice(start, end));
        }
        start = end;
      }
      return columns;
    },
    buildOutcomeColumns(odds: PreviewOdd[]): PreviewOutcome[][] {
      const selected = this.getOddsForSpread(odds);
      const outcomes = (selected?.outcome || []) as PreviewOutcome[];
      if (
        odds[0].id == this.correctMarket ||
        odds[0].id == this.correctMarket2
      ) {
        return this.getCorrectData(outcomes);
      }
      if (outcomes.length > 5) {
        const total = outcomes.length;
        let cols = 1;
        if (total % 3 === 0) {
          cols = 3;
        } else if (total % 2 === 0) {
          cols = 2;
        }
        return this.distributeIntoColumns(outcomes, cols);
      }
      return outcomes.map(outcome => [outcome]);
    },
    getOddsForSpread(odds: PreviewOdd[]): PreviewOdd {
      if (!Array.isArray(odds) || odds.length === 0) return null as any;
      if (odds.length === 1) return odds[0];
      // Prendi il primo odd con specifier valido, altrimenti il primo
      const found = (odds as any[]).find((odd: any) => {
        const spec = String(odd?.specifier || '');
        return spec && spec !== 'noSpec';
      });
      return (found as any) || odds[0];
    },
    getFilteredOddGroup(oddGroup: PreviewOdd[]): PreviewOdd[] {
      if (!Array.isArray(oddGroup) || oddGroup.length === 0) return [];
      // Restituisci sempre tutto il gruppo
      return oddGroup;
    },
    async loadTournamentForCategory(categoryID: string) {
      this.isTournamentsListLoading = true;
      await this.$store.dispatch(
        'IllinoisSport/fetchTournamentsForCategoryIfNeeded',
        { sportId: this.sportId, categoryId: categoryID }
      );

      this.isTournamentsListLoading = false;
    },
    onResize() {
      this.windowHeight = window.innerHeight;
    },
    getCurrentPeriod(period: any[]): string {
      if (!Array.isArray(period) || period.length === 0) {
        return '';
      }
      const find = period.find((p: any) => p.lang === this.currentLanguage);
      if (find) {
        return find.text || '';
      }
      const findEn = period.find((p: any) => p.lang === 'en');
      if (findEn) {
        return findEn.text || '';
      }
      return '';
    }
  },
  computed: {
    skeletonEvent(): any {
      return {
        home: '',
        away: '',
        scheduled: new Date().toISOString()
      } as any;
    },
    skeletonOdds(): any[] {
      return [[{}], [{}], [{}]] as any[];
    },
    event(): LiveEventDetailsEvent | null {
      const live = this.$store.getters['IllinoisSport/getLiveEventById'](
        this.eventId
      );
      return (live && live.event) || null;
    },
    liveEventsBySport(): LiveEventsCountResult | null {
      return this.$store.getters['IllinoisSport/getLiveEventsBySport'](
        this.sportIdSelected
      );
    },
    marketGroups(): { label: string; value: string }[] {
      const live = this.$store.getters['IllinoisSport/getLiveEventById'](
        this.eventId
      );
      const groups = (live && live.marketsGroups) || [];
      return groups.map((g: any) => ({ label: g.name, value: g._id }));
    },

    odds(): PreviewOdd[][] {
      // Ora le odds sono un oggetto in event.odds, keyed per unique_id
      const currentEvent = this.event as any;
      const oddsObj = (currentEvent && currentEvent.odds) || {};
      const list: any[] = Object.values(oddsObj);
      if (!Array.isArray(list) || list.length === 0) return [];

      // raggruppa per id mercato e mantieni le varianti per specifier
      const grouped: Record<string, PreviewOdd[]> = {};
      list.forEach((o: any) => {
        // outcome è ora un oggetto: converti a array per la UI senza mutare lo store
        const outcomesArray = Array.isArray(o?.outcome)
          ? o.outcome
          : Object.values(o?.outcome || {});
        // Filtra gli outcome con status !== 3
        const filteredOutcomes = outcomesArray.filter(
          (outcome: any) => outcome.status !== 3
        );
        // Se dopo il filtraggio l'odd è vuota, non aggiungerla
        if (filteredOutcomes.length === 0) return;
        const normalizedOdd = { ...o, outcome: filteredOutcomes };
        console.log('normalizedOdd', normalizedOdd);
        const key = String(normalizedOdd.id);
        if (!grouped[key]) grouped[key] = [];
        grouped[key].push(normalizedOdd as PreviewOdd);
      });
      console.log('grouped', grouped);
      const orderedGroup = Object.values(grouped).sort(
        (a: PreviewOdd[], b: PreviewOdd[]) => {
          return a[0].sort - b[0].sort;
        }
      );
      return orderedGroup;
    },
    tournaments(): Tournament[] {
      return this.$store.getters['IllinoisSport/getTournamentsByCategory'](
        this.categoryId
      );
    },
    events(): PreviewEventGroup[] {
      return this.$store.getters['IllinoisSport/getEventsByTournament'](
        this.tournamentSelectedId
      );
    },
    selectedCategoryName(): string {
      const categories = this.$store.getters[
        'IllinoisSport/getCategoriesBySport'
      ](this.sportId);
      const categoryName = categories.find(
        (category: any) => category.categoryId.toString() === this.categoryId
      )?.name;
      return categoryName || '';
    },
    currentLanguage(): string {
      return this.$store.getters['settings/getLanguage'] || 'en';
    },
    tournamentIdProp(): string {
      return this.tournamentId;
    }
  },
  async mounted() {
    this.loading = true;
    window.addEventListener('resize', this.onResize, {
      passive: true
    });
    this.$store.dispatch('IllinoisSport/setFieldStatistics', {
      sportId: this.sportId,
      eventId: this.eventId
    });
    // Caricamenti in background
    this.$store.dispatch(
      'IllinoisSport/fetchLiveEventsCountBySport',
      {} as any
    );
    this.$store.dispatch('IllinoisSport/fetchCategoriesIfNeeded', {
      sportId: this.sportId
    });
    // Imposta subito la modalità live detail e mostra il tracker, così il watcher di LiveMatchTracker scatta
    await this.$store.dispatch('odds/setLiveMode', { mode: 'detail' });
    await this.$store.dispatch('odds/setViewLiveMatchTracker', true);
    // Carica i dettagli evento e appena pronto rimuovi lo stato di loading
    await this.$store.dispatch('IllinoisSport/fetchLiveEventDetailsLive', {
      sportId: this.sportId,
      categoryId: this.categoryId,
      tournamentId: this.tournamentId,
      eventId: this.eventId,
      groupId: null
    });
    this.loading = false;
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize);
  },
  watch: {
    marketGroups: {
      immediate: true,
      handler(groups) {
        const groupIds = (groups || []).map((g: { value: string }) => g.value);
        if (
          (!this.activeMarketGroup && groups && groups.length) ||
          (this.activeMarketGroup && !groupIds.includes(this.activeMarketGroup))
        ) {
          this.activeMarketGroup =
            groups && groups.length ? groups[0].value : null;
        }
      }
    },
    async activeMarketGroup(newVal) {
      if (newVal && !this.loading) {
        this.isGroupOddsLoading = true;
        await this.$store.dispatch('IllinoisSport/fetchLiveEventGroupOdds', {
          sportId: this.sportId,
          categoryId: this.categoryId,
          tournamentId: this.tournamentId,
          eventId: this.eventId,
          groupId: newVal
        });
        this.isGroupOddsLoading = false;
      }
    },
    eventId: {
      handler(newVal) {
        // Reset stato legato all'evento precedente per forzare il ricalcolo
        this.openChangeGameMenu = false;
        this.showTournamentsList = true;
        this.tournamentSelectedId = this.tournamentId;
        this.tournamentSelectedName = '';
        this.activeMarketGroup = null;
        this.openTitleMenu = false;
        // Imposta prima la modalità e la visibilità del tracker per garantire il trigger del watcher
        this.$store.dispatch('odds/setLiveMode', { mode: 'detail' });
        this.$store.dispatch('odds/setViewLiveMatchTracker', true);
        this.$store.dispatch('IllinoisSport/fetchLiveEventDetailsLive', {
          sportId: this.sportId,
          categoryId: this.categoryId,
          tournamentId: this.tournamentId,
          eventId: newVal,
          groupId: null
        });
      }
    }
  }
});
